import { useState, useMemo } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { categories, businesses } from '../../mocks/localGuide';
import KeepExploring from '../../components/feature/KeepExploring';
import SEO from '../../components/feature/SEO';

export default function LocalGuidePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const siteUrl = import.meta.env.VITE_SITE_URL ?? '';

  const filteredBusinesses = useMemo(() => {
    return businesses.filter((business) => {
      const matchesSearch = 
        business.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        business.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        business.location.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = 
        selectedCategory === 'all' || business.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory]);

  const getCategoryInfo = (categoryId: string) => {
    return categories.find(cat => cat.id === categoryId);
  };

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Local Mom Resources & Business Guide | Gwinnett County',
    description:
      'Trusted local businesses recommended by Gwinnett County moms. Browse mom-approved services, shops, and resources.',
    url: `${siteUrl}/local-guide`,
    publisher: {
      '@type': 'Organization',
      name: 'The Mommy Mingle',
      url: siteUrl,
    },
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Local Mom Resources & Business Guide | Gwinnett County | The Mommy Mingle"
        description="Find trusted local businesses recommended by Gwinnett County moms. Browse mom-approved services, shops, and resources across Gwinnett and surrounding areas."
        keywords="local business guide Gwinnett, mom approved businesses Georgia, trusted local services Gwinnett County"
        canonical="/local-guide"
        schema={schema}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50 pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              Local Mom Resources
            </h1>
            <p className="text-xl text-gray-700">
              Discover trusted local businesses recommended by moms in Sugar Hill, Gwinnett County, and surrounding communities.
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
              <input
                type="text"
                placeholder="Search businesses, services, or locations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-full border-2 border-gray-200 focus:border-rose-400 focus:outline-none text-base"
              />
            </div>
          </div>

          {/* Category Filter Pills */}
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-6 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                selectedCategory === 'all'
                  ? 'bg-rose-500 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              All Categories
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === category.id
                    ? `${category.color}`
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Results Count */}
      <section className="py-8 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-gray-600 text-base">
            Showing <strong className="text-gray-900">{filteredBusinesses.length}</strong> {filteredBusinesses.length === 1 ? 'business' : 'businesses'}
            {selectedCategory !== 'all' && (
              <span> in <strong className="text-gray-900">{getCategoryInfo(selectedCategory)?.name}</strong></span>
            )}
          </p>
        </div>
      </section>

      {/* Business Listings Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredBusinesses.length === 0 ? (
            <div className="text-center py-16">
              <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
              <h3 className="text-2xl font-semibold text-gray-900 mb-2">No businesses found</h3>
              <p className="text-gray-600">Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredBusinesses.map((business) => {
                const categoryInfo = getCategoryInfo(business.category);
                return (
                  <div
                    key={business.id}
                    className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300 cursor-pointer group"
                  >
                    {/* Business Photo */}
                    <div className="relative h-56 overflow-hidden">
                      <img
                        src={business.photos[0]}
                        alt={business.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      {/* Category Pill */}
                      {categoryInfo && (
                        <div className={`absolute top-4 left-4 px-4 py-1 rounded-full text-sm font-medium ${categoryInfo.color}`}>
                          {categoryInfo.name}
                        </div>
                      )}
                    </div>

                    {/* Business Info */}
                    <div className="p-6">
                      {/* Name and Rating */}
                      <div className="mb-3">
                        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-rose-500 transition-colors">
                          {business.name}
                        </h3>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center">
                            {[...Array(5)].map((_, i) => (
                              <i
                                key={i}
                                className={`${
                                  i < Math.floor(business.averageRating)
                                    ? 'ri-star-fill text-amber-400'
                                    : 'ri-star-line text-gray-300'
                                } text-base`}
                              ></i>
                            ))}
                          </div>
                          <span className="text-sm text-gray-600">
                            {business.averageRating.toFixed(1)} ({business.reviewCount} reviews)
                          </span>
                        </div>
                      </div>

                      {/* Location */}
                      <div className="flex items-center gap-2 text-gray-600 mb-3">
                        <i className="ri-map-pin-line text-base"></i>
                        <span className="text-sm">{business.location}</span>
                      </div>

                      {/* Description */}
                      <p className="text-gray-700 text-sm mb-4 line-clamp-3">
                        {business.description}
                      </p>

                      {/* Social Links */}
                      <div className="flex items-center gap-3 mb-4">
                        {business.website && (
                          <a
                            href={business.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-rose-500 hover:text-white transition-colors cursor-pointer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <i className="ri-global-line text-base"></i>
                          </a>
                        )}
                        {business.socialLinks?.instagram && (
                          <a
                            href={business.socialLinks.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-rose-500 hover:text-white transition-colors cursor-pointer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <i className="ri-instagram-line text-base"></i>
                          </a>
                        )}
                        {business.socialLinks?.facebook && (
                          <a
                            href={business.socialLinks.facebook}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-rose-500 hover:text-white transition-colors cursor-pointer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <i className="ri-facebook-line text-base"></i>
                          </a>
                        )}
                      </div>

                      {/* Member Discount Badge */}
                      {business.isPartner && business.memberDiscount && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <div className="flex items-start gap-2">
                            <i className="ri-vip-crown-line text-green-600 text-lg mt-0.5"></i>
                            <div>
                              <p className="text-xs font-semibold text-green-800 mb-1">
                                Mommy Mingle Member Discount
                              </p>
                              <p className="text-sm text-green-700">
                                {business.memberDiscount}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <KeepExploring
        title="Keep Exploring"
        subtitle="Find more ways to enjoy life in Sugar Hill, Gwinnett, and surrounding areas."
        items={[
          { label: 'View Local Events', icon: 'ri-calendar-event-line', to: '/events' },
          { label: 'Explore Local Resources', icon: 'ri-heart-2-line', to: '/local-resources' },
          { label: 'Join The Mommy Mingle', icon: 'ri-group-line', href: 'https://www.facebook.com/groups/themommymingle', primary: true },
        ]}
        bg="soft"
      />

      <Footer />
    </div>
  );
}